// src/components/AdminForm.jsx
import React, { useState, useEffect } from 'react';

const AdminForm = ({ onAdd, editData, onCancel }) => {
  const [formData, setFormData] = useState({ name: '', details: '', tech: '', link: '' });
  const [images, setImages] = useState([]);
  const [isCompressing, setIsCompressing] = useState(false);

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || '',
        details: editData.details || '',
        tech: editData.tech?.join(', ') || '',
        link: editData.link || ''
      });
      setImages(editData.images || []);
    } else {
      setFormData({ name: '', details: '', tech: '', link: '' });
      setImages([]);
    }
  }, [editData]);

  // Handle Image Upload with Auto-Compression
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsCompressing(true);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800; // Resize for performance
          const scaleSize = MAX_WIDTH / img.width;
          
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Compress to 70% quality JPEG
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          setImages(prev => [...prev, compressedBase64]);
          setIsCompressing(false);
        };
      };
    });
  };

  const removeImage = (indexToRemove) => {
    setImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name) return alert("Project name is required!");

    const project = {
      ...formData,
      tech: formData.tech.split(',').map(t => t.trim()).filter(t => t !== ""),
      images: images,
    };

    onAdd(project);
    if (!editData) {
      setFormData({ name: '', details: '', tech: '', link: '' });
      setImages([]);
    }
  };

  const inputStyle = { 
    width: '100%', padding: '12px', marginBottom: '10px', 
    backgroundColor: '#1e293b', border: '1px solid #334155', 
    borderRadius: '8px', color: 'white', outline: 'none' 
  };

  return (
    <form 
      onSubmit={handleSubmit} 
      style={{ 
        backgroundColor: '#0f172a', padding: '20px', borderRadius: '12px', 
        border: editData ? '2px solid #3b82f6' : '1px solid #1e293b',
        boxShadow: editData ? '0 0 20px rgba(59, 130, 246, 0.2)' : 'none'
      }}
    >
      <p style={{ color: editData ? '#60a5fa' : '#3b82f6', fontSize: '11px', fontWeight: 'bold', marginBottom: '15px' }}>
        {editData ? "⚡ EDITING MODE" : "✨ NEW PROJECT"}
      </p>

      <input style={inputStyle} placeholder="Project Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
      <textarea style={{...inputStyle, height: '80px', resize: 'none'}} placeholder="Project Details" value={formData.details} onChange={(e) => setFormData({...formData, details: e.target.value})} />
      <input style={inputStyle} placeholder="Tech Stack (React, Firebase, CSS)" value={formData.tech} onChange={(e) => setFormData({...formData, tech: e.target.value})} />
      <input style={inputStyle} placeholder="Project Live Link" value={formData.link} onChange={(e) => setFormData({...formData, link: e.target.value})} />
      
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', color: '#94a3b8', fontSize: '11px', marginBottom: '8px' }}>
          {isCompressing ? "⌛ Optimizing images..." : "Upload Images (Auto-Compressed)"}
        </label>
        <input 
          type="file" multiple accept="image/*" 
          onChange={handleImageChange} 
          style={{ 
            color: '#94a3b8', fontSize: '12px', width: '100%', 
            padding: '10px', border: '1px dashed #334155', borderRadius: '8px' 
          }} 
        />
      </div>

      {/* Optimized Image Preview Grid */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {images.map((img, i) => (
          <div key={i} style={{ position: 'relative', width: '70px', height: '70px', group: 'true' }}>
            <img 
              src={img} 
              alt="preview" 
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', border: '2px solid #334155' }} 
            />
            <button
              type="button"
              onClick={() => removeImage(i)}
              title="Remove Image"
              style={{
                position: 'absolute', top: '-6px', right: '-6px',
                backgroundColor: '#ef4444', color: 'white', border: 'none',
                borderRadius: '50%', width: '20px', height: '20px',
                fontSize: '10px', cursor: 'pointer', fontWeight: 'bold'
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <button 
        type="submit" 
        disabled={isCompressing}
        style={{ 
          width: '100%', padding: '14px', 
          backgroundColor: isCompressing ? '#1e293b' : (editData ? '#10b981' : '#3b82f6'), 
          color: 'white', border: 'none', borderRadius: '8px', 
          fontWeight: 'bold', cursor: isCompressing ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s'
        }}
      >
        {isCompressing ? "Processing..." : (editData ? "Update Project" : "Publish Project")}
      </button>

      {editData && (
        <button 
          type="button" 
          onClick={onCancel} 
          style={{ width: '100%', marginTop: '10px', background: 'none', color: '#94a3b8', border: 'none', cursor: 'pointer', fontSize: '12px' }}
        >
          Cancel and Clear
        </button>
      )}
    </form>
  );
};

export default AdminForm;